import type { SpreadBackgroundPlacements } from '@/lib/backgrounds/types/spread-background-placements'
import type { BackgroundDropSlot } from '@/lib/backgrounds/types/background-drop-slot'
import type { SpreadPageRects } from '@/lib/backgrounds/types/spread-page-rects'
import { unionSpreadPageRect } from '@/lib/backgrounds/spread-page-rects'
import type { BackgroundOptionsItem } from './types/background-options-item'
import type { BackgroundOptionsLayout } from './types/background-options-layout'

const SLOT_LABEL: Record<BackgroundDropSlot, string> = {
  double: 'Spread tint',
  left: 'Left tint',
  right: 'Right tint',
}

/**
 * One options toolbar per placed background: a single one for a spread background, otherwise one
 * per page that carries a background. `double` and `left`/`right` are mutually exclusive
 * (`applyBackgroundPlacement`), so a spread background never shows alongside page toolbars.
 * Returns null when nothing is placed, or the spread has no page to anchor to.
 */
export function resolveBackgroundOptionsLayout(
  placements: SpreadBackgroundPlacements,
  pageRects: SpreadPageRects,
  bleedMargin: number
): BackgroundOptionsLayout | null {
  const union = unionSpreadPageRect(pageRects)

  if (!union) return null

  const items: BackgroundOptionsItem[] = placements.double
    ? [{ slot: 'double', label: SLOT_LABEL.double, align: 'end' }]
    : (['left', 'right'] as const)
        .filter((slot) => placements[slot] && pageRects[slot])
        .map((slot) => ({
          slot,
          label: SLOT_LABEL[slot],
          align: slot === 'left' ? 'endAtSpine' : 'endOrSpine',
        }))

  if (items.length === 0) return null

  return {
    anchor: {
      x: union.x - bleedMargin,
      y: union.y - bleedMargin,
      width: union.width + bleedMargin * 2,
      height: union.height + bleedMargin * 2,
    },
    spineX: pageRects.left ? pageRects.left.x + pageRects.left.width : union.x,
    items,
  }
}
